<template>
  <main
    v-if="freightOrder"
    class="freight-order__main d-flex mx-0 px-0"
  >
    <left-sidebar
      :show-sidebar="showSidebar"
      :document-nav="documentNavigation"
    />
    <div
      class="main__form mb-5 px-4"
    >
      <form @submit.prevent>
        <div
          class="form__top-buttons mt-2"
        >
          <router-link
            to="/freight-orders"
          >
            <dx-button
              icon="ace-icon icon-back"
              :text="$t('Back')"
              type="normal"
              styling-mode="text"
              class="back-button"
            />
          </router-link>
          <dx-button
            :text="showSidebar ? $t('Hide navigation') : $t('Show navigation')"
            type="normal"
            styling-mode="text"
            class="showbar-button"
            @click="sidebarLocale"
          />
        </div>
        <b-row class="mb-3">
          <b-col
            class="d-flex flex-row"
          >
            <div
              :class="classesForStatuses[currentStatus.code]"
              class="status-block mr-2"
            >
              {{ $t(currentStatus.name) }}
            </div>
            <div
              v-if="freightOrder.is_paid"
              class="paid-block is-paid"
            >
              {{ $t('is_paid') }}
            </div>
          </b-col>
        </b-row>
        <b-row>
          <b-col lg="8">
            <h3 class="mb-4">
              {{ pageTitle }}
            </h3>
          </b-col>
        </b-row>
        <div class="form__group">
          <dx-fieldset-header
            id="order"
            :text="$t('Order')"
          />
          <b-row>
            <b-col
              v-if="user.can_select_company"
              lg="5"
            >
              <dx-form-group :label="$t('Client')">
                <dx-select-box
                  v-model="freightOrder.company"
                  :data-source="companies"
                  value-expr="id"
                  :read-only="readOnly"
                  :display-expr="companyDisplayExpr"
                  :validation-status="freightOrderErrors.company ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.company }"
                  :drop-down-options="dropDownOptions"
                  :use-item-text-as-title="true"
                  styling-mode="outlined"
                  @selection-changed="resetFreightOrderValidation('company')"
                  @itemClick="updateCompanyInfo"
                />
              </dx-form-group>
            </b-col>
            <b-col
              v-if="user.is_manager"
              lg="2"
            >
              <dx-form-group :label="$t('Contract')">
                <dx-text-box
                  v-model="freightOrder.freight_forwarding_contract"
                  value-change-event="input"
                  styling-mode="outlined"
                  :max-length="45"
                  :validation-status="freightOrderErrors.freight_forwarding_contract ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.freight_forwarding_contract }"
                  @input="resetFreightOrderValidation('freight_forwarding_contract')"
                />
              </dx-form-group>
            </b-col>
            <b-col
              v-if="user.is_manager"
              lg="3"
            >
              <dx-form-group :label="$t('Contract date')">
                <dx-date-box
                  v-model="freightOrder.freight_forwarding_contract_date"
                  date-serialization-format="yyyy-MM-dd"
                  type="date"
                  class="w-100"
                  styling-mode="outlined"
                  :validation-status="freightOrderErrors.freight_forwarding_contract_date ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.freight_forwarding_contract_date }"
                  @change="resetFreightOrderValidation('freight_forwarding_contract_date')"
                />
              </dx-form-group>
            </b-col>
            <b-col
              lg="2"
            >
              <dx-form-group :label="$t('Customer shipping order number')">
                <dx-text-box
                  v-model="freightOrder.customer_order_number"
                  :read-only="readOnly"
                  value-change-event="input"
                  styling-mode="outlined"
                  :max-length="50"
                  :validation-status="freightOrderErrors.customer_order_number ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.customer_order_number }"
                  @input="resetFreightOrderValidation('customer_order_number')"
                />
              </dx-form-group>
            </b-col>
          </b-row>
        </div>


        <div class="form__group">
          <dx-fieldset-header
            id="consignor-consignee"
            :text="$t('Consignor / Consignee')"
          />
          <b-row class="mb-1">
            <b-col lg="6">
              <dx-form-group :label="$t('Consignor')">
                <dx-text-box
                  v-model="freightOrder.consignor"
                  :read-only="readOnly"
                  value-change-event="input"
                  styling-mode="outlined"
                  :max-length="150"
                  :validation-status="freightOrderErrors.consignor ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.consignor }"
                  @input="resetFreightOrderValidation('consignor')"
                >
                  <dx-text-box-button
                    name="consignor"
                    :options="consignorButton"
                    location="after"
                  />
                </dx-text-box>
              </dx-form-group>
            </b-col>

            <b-col lg="6">
              <div>
                <dx-form-group :label="$t('Consignee')">
                  <dx-text-box
                    v-model="freightOrder.consignee"
                    :read-only="readOnly"
                    value-change-event="input"
                    styling-mode="outlined"
                    :max-length="150"
                    :validation-status="freightOrderErrors.consignee ? 'invalid' : 'valid'"
                    :validation-error="{ message: freightOrderErrors.consignee }"
                    @input="resetFreightOrderValidation('consignee')"
                  >
                    <dx-text-box-button
                      name="consignee"
                      :options="consigneeButton"
                      location="after"
                    />
                  </dx-text-box>
                </dx-form-group>
              </div>
            </b-col>
          </b-row>
          <b-row>
            <b-col lg="12">
              <i
                class="dx-dropdowneditor-icon custom-dropdown"
                :class="{'active': isConsignorCollapseShown}"
                @click="isConsignorCollapseShown = !isConsignorCollapseShown"
              />
            </b-col>
          </b-row>
          <b-row>
            <b-col>
              <b-collapse v-model="isConsignorCollapseShown">
                <b-row>
                  <b-col
                    v-for="it in consigmentFields"
                    :key="it.key"
                    :lg="it.lg"
                  >
                    <dx-form-group :label="it.label">
                      <dx-text-box
                        v-if="it.key !== 'countrycode'"
                        v-model="freightOrder[`consignor_${it.key}`]"
                        :read-only="readOnly"
                        value-change-event="input"
                        styling-mode="outlined"
                        :max-length="it.maxLength"
                        :validation-status="freightOrderErrors[`consignor_${it.key}`] ? 'invalid' : 'valid'"
                        :validation-error="{ message: freightOrderErrors[`consignor_${it.key}`] }"
                        @input="resetFreightOrderValidation(`consignor_${it.key}`)"
                      />
                      <dx-select-box
                        v-else
                        v-model="freightOrder[`consignor_${it.key}`]"
                        :read-only="readOnly"
                        :search-enabled="true"
                        :show-clear-button="true"
                        :data-source="countries"
                        :paginate="true"
                        value-expr="abc2"
                        :display-expr="countryDisplayExpr"
                        styling-mode="outlined"
                        :max-length="it.maxLength"
                        :validation-status="freightOrderErrors[`consignor_${it.key}`] ? 'invalid' : 'valid'"
                        :validation-error="{ message: freightOrderErrors[`consignor_${it.key}`] }"
                        @selection-changed="resetFreightOrderValidation(`consignor_${it.key}`)"
                      />
                    </dx-form-group>
                  </b-col>
                </b-row>
              </b-collapse>
            </b-col>
            <b-col>
              <b-collapse v-model="isConsignorCollapseShown">
                <b-row>
                  <b-col
                    v-for="it in consigmentFields"
                    :key="it.key"
                    :lg="it.lg"
                  >
                    <dx-form-group :label="it.label">
                      <dx-text-box
                        v-if="it.key !== 'countrycode'"
                        v-model="freightOrder[`consignee_${it.key}`]"
                        :read-only="readOnly"
                        value-change-event="input"
                        styling-mode="outlined"
                        :max-length="it.maxLength"
                        :validation-status="freightOrderErrors[`consignee_${it.key}`] ? 'invalid' : 'valid'"
                        :validation-error="{ message: freightOrderErrors[`consignee_${it.key}`] }"
                        @input="resetFreightOrderValidation(`consignee_${it.key}`)"
                      />
                      <dx-select-box
                        v-else
                        v-model="freightOrder[`consignee_${it.key}`]"
                        :read-only="readOnly"
                        :search-enabled="true"
                        :show-clear-button="true"
                        :data-source="countries"
                        :paginate="true"
                        value-expr="abc2"
                        :display-expr="countryDisplayExpr"
                        styling-mode="outlined"
                        :max-length="it.maxLength"
                        :validation-status="freightOrderErrors[`consignee_${it.key}`] ? 'invalid' : 'valid'"
                        :validation-error="{ message: freightOrderErrors[`consignee_${it.key}`] }"
                        @selection-changed="resetFreightOrderValidation(`consignee_${it.key}`)"
                      />
                    </dx-form-group>
                  </b-col>
                </b-row>
              </b-collapse>
            </b-col>
          </b-row>
          <b-row>
            <b-col cols="12">
              <dx-form-group :label="$t('Notify')">
                <dx-autocomplete
                  v-model="freightOrder.notify"
                  :read-only="readOnly"
                  :min-search-length="0"
                  :max-length="2000"
                  styling-mode="outlined"
                  :data-source="notify_datasource"
                  :validation-status="freightOrderErrors.notify ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.notify }"
                  @input="resetFreightOrderValidation('notify')"
                  @item-click="resetFreightOrderValidation('notify')"
                  @focusIn="onAutocompleteFocusIn($event, 'notify')"
                />
              </dx-form-group>
            </b-col>
          </b-row>
        </div>

        <div class="form__group">
          <dx-fieldset-header
            id="fright-details"
            :text="$t('Freight details')"
          />
          <b-row>
            <b-col lg="6">
              <dx-form-group :label="$t('ETSNG')">
                <dx-select-box
                  v-model="freightOrder.etsng_code"
                  :read-only="readOnly"
                  :search-enabled="true"
                  :show-clear-button="true"
                  :data-source="refbookETSNG"
                  :paginate="true"
                  value-expr="code"
                  :display-expr="refBookDisplayExpr"
                  styling-mode="outlined"
                  :validation-status="freightOrderErrors.etsng_code ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.etsng_code }"
                  @selection-changed="updateFreightOrderDescription('ETSNG', $event); resetFreightOrderValidation('etsng_code')"
                />
              </dx-form-group>
            </b-col>
            <b-col lg="6">
              <dx-form-group :label="$t('GNG')">
                <dx-select-box
                  v-model="freightOrder.gng_code"
                  :read-only="readOnly"
                  :search-enabled="true"
                  :show-clear-button="true"
                  :data-source="refbookGNG"
                  :paginate="true"
                  value-expr="code"
                  :display-expr="refBookDisplayExpr"
                  styling-mode="outlined"
                  :validation-status="freightOrderErrors.gng_code ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.gng_code }"
                  @selection-changed="updateFreightOrderDescription('GNG', $event); resetFreightOrderValidation('gng_code')"
                />
              </dx-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col lg="2">
              <dx-form-group :label="$t('Freight cost')">
                <dx-text-box
                  v-model="freightOrder.freight_cost"
                  :read-only="readOnly"
                  value-change-event="input"
                  styling-mode="outlined"
                  :validation-status="freightOrderErrors.freight_cost ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.freight_cost }"
                  @input="resetFreightOrderValidation('freight_cost')"
                />
              </dx-form-group>
            </b-col>
            <b-col lg="2">
              <dx-form-group :label="$t('Currency')">
                <dx-text-box
                  v-model="freightOrder.freight_cost_currency"
                  :read-only="readOnly"
                  value-change-event="input"
                  styling-mode="outlined"
                  :validation-status="freightOrderErrors.freight_cost_currency ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.freight_cost_currency }"
                  @input="resetFreightOrderValidation('freight_cost_currency')"
                />
              </dx-form-group>
            </b-col>
            <b-col lg="2">
              <dx-form-group :label="$t('freight gross weight')+', '+$t('kg')">
                <dx-text-box
                  v-model="freightOrder.freight_gross_weight"
                  :read-only="readOnly"
                  value-change-event="input"
                  styling-mode="outlined"
                  :validation-status="freightOrderErrors.freight_gross_weight ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.freight_gross_weight }"
                  @input="resetFreightOrderValidation('freight_gross_weight')"
                />
              </dx-form-group>
            </b-col>
            <b-col lg="2">
              <dx-form-group :label="$t('freight net weight')+', '+$t('kg')">
                <dx-text-box
                  v-model="freightOrder.freight_net_weight"
                  :read-only="readOnly"
                  value-change-event="input"
                  styling-mode="outlined"
                  :validation-status="freightOrderErrors.freight_net_weight ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.freight_net_weight }"
                  @input="resetFreightOrderValidation('freight_net_weight')"
                />
              </dx-form-group>
            </b-col>
            <b-col>
              <dx-form-group :label="$t('Count and packing kind')">
                <dx-autocomplete
                  v-model="freightOrder.packing_kind"
                  :read-only="readOnly"
                  :min-search-length="0"
                  :max-length="100"
                  styling-mode="outlined"
                  :data-source="packing_kind_datasource"
                  :validation-status="freightOrderErrors.packing_kind ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.packing_kind }"
                  @input="resetFreightOrderValidation('packing_kind')"
                  @item-click="resetFreightOrderValidation('packing_kind')"
                  @focusIn="onAutocompleteFocusIn($event, 'packing_kind')"
                />
              </dx-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col
              lg="6"
              class="mb-4 mb-md-4 mb-lg-0"
            >
              <dx-form-group
                :label="$t('Freight description')"
                :margin-bottom="false"
                class="dx-form-group-select"
                :autocomplete="!readOnly"
                @select="showAutocompleteModal('freight_description')"
              >
                <dx-text-area
                  v-model="freightOrder.freight_description"
                  :read-only="readOnly"
                  value-change-event="input"
                  height="120"
                  styling-mode="outlined"
                  :max-length="1000"
                  :validation-status="freightOrderErrors.freight_description ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.freight_description }"
                  @input="resetFreightOrderValidation('freight_description')"
                />
              </dx-form-group>
            </b-col>
            <b-col lg="6">
              <dx-form-group
                :label="$t('Notes with empty return instruction')"
                :margin-bottom="false"
                class="dx-form-group-select"
                :autocomplete="!readOnly"
                @select="showAutocompleteModal('notes')"
              >
                <dx-text-area
                  v-model="freightOrder.notes"
                  :read-only="readOnly"
                  value-change-event="input"
                  height="120"
                  styling-mode="outlined"
                  :max-length="3000"
                  :validation-status="freightOrderErrors.notes ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.notes }"
                  @input="resetFreightOrderValidation('notes')"
                />
              </dx-form-group>
            </b-col>
          </b-row>

          <!-- <b-row>
              <b-col lg="4">
                <dx-form-group :label="$t('shipment_kind')">
                  <dx-select-box
                    v-model="freightOrder.shipment_kind"
                    :read-only="readOnly"
                    :search-enabled="true"
                    :data-source="shipmentKinds"
                    value-expr="id"
                    display-expr="description"
                    styling-mode="filled"
                    :validation-status="freightOrderErrors.shipment_kind ? 'invalid' : 'valid'"
                    :validation-error="{ message: freightOrderErrors.direction }"
                    @selection-changed="resetFreightOrderValidation('shipment_kind')"
                  />
                </dx-form-group>
              </b-col>
            </b-row> -->
        </div>

        <div class="form__group">
          <dx-fieldset-header
            id="shipping-period"
            :text="$t('Shipping period')"
          />
          <b-row>
            <b-col lg="3">
              <dx-form-group :label="$t('From')">
                <dx-date-box
                  v-model="freightOrder.preferred_shipping_period_start"
                  date-serialization-format="yyyy-MM-dd"
                  type="date"
                  class="w-100"
                  styling-mode="outlined"
                  :validation-status="freightOrderErrors.preferred_shipping_period_start ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.preferred_shipping_period_start }"
                  @change="resetFreightOrderValidation('preferred_shipping_period_start')"
                />
              </dx-form-group>
            </b-col>
            <b-col lg="3">
              <dx-form-group :label="$t('To')">
                <dx-date-box
                  v-model="freightOrder.preferred_shipping_period_end"
                  date-serialization-format="yyyy-MM-dd"
                  type="date"
                  class="w-100"
                  styling-mode="outlined"
                  :validation-status="freightOrderErrors.preferred_shipping_period_end ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.preferred_shipping_period_end }"
                  @change="resetFreightOrderValidation('preferred_shipping_period_end')"
                />
              </dx-form-group>
            </b-col>
            <b-col lg="3">
              <dx-form-group :label="$t('container_type')">
                <dx-select-box
                  v-model="freightOrder.container_type"
                  :read-only="readOnly"
                  :search-enabled="true"
                  :data-source="containerTypes"
                  value-expr="id"
                  display-expr="description"
                  styling-mode="outlined"
                  :validation-status="freightOrderErrors.container_type ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.container_type }"
                  @selection-changed="resetFreightOrderValidation('container_type')"
                />
              </dx-form-group>
            </b-col>
            <b-col lg="3">
              <dx-form-group :label="$t('container_count')">
                <dx-number-box
                  v-model="freightOrder.container_count"
                  :read-only="readOnly"
                  value-change-event="input"
                  styling-mode="outlined"
                  :validation-status="freightOrderErrors.container_count ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.container_count }"
                  @input="resetFreightOrderValidation('container_count')"
                />
              </dx-form-group>
            </b-col>
          </b-row>
          <b-row>
            <b-col lg="12">
              <dx-form-group
                :label="$t('important notes')"
                :margin-bottom="false"
                class="dx-form-group-select"
                :autocomplete="!readOnly"
                @select="showAutocompleteModal('important_note')"
              >
                <dx-text-area
                  v-model="freightOrder.important_note"
                  :read-only="readOnly"
                  value-change-event="input"
                  height="120"
                  styling-mode="outlined"
                  :max-length="3000"
                  :validation-status="freightOrderErrors.important_note ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.important_note }"
                  @input="resetFreightOrderValidation('important_note')"
                />
              </dx-form-group>
            </b-col>
          </b-row>
        </div>

        <div class="form__group">
          <b-row
            class="d-flex flex-row justify-content-between align-items-center mb-3"
          >
            <b-col>
              <dx-fieldset-header
                id="services"
                :text="$t('Services')"
                class="mb-0"
              />
            </b-col>
            <b-col>
              <dx-button
                :text="$t('Add service')"
                icon="ace-icon icon-plus"
                type="success"
                styling-mode="contained"
                class="custom-btn float-right"
                :disabled="services.length === servicesSelected.length"
                @click="isServiceModalShown = true"
              />
            </b-col>
          </b-row>
          <dx-data-grid
            v-if="freightOrder"
            ref="servicesGrid"
            class="mb-4"
            :data-source="servicesSelected"
            :show-borders="false"
            :column-min-width="50"
            :allow-column-resizing="true"
            column-resizing-mode="widget"
            :hover-state-enabled="true"
            :no-data-text="$t('Please, select a transport, shipment kind and services')"
            @row-removed="removeFreightOrderedService"
          >
            <!-- <DxScrolling row-rendering-mode="virtual" />
              <dx-paging :page-size="10" />
              <dx-pager
                :visible="true"
                :allowed-page-sizes="pageSizes"
                :display-mode="displayMode"
                :show-page-size-selector="showPageSizeSelector"
                :show-info="showInfo"
                :show-navigation-buttons="showNavButtons"
              /> -->
            <dx-column
              data-field="id"
              :visible="false"
            />
            <dx-column
              data-field="shipment_kind"
              :caption="$t('shipment_kind')"
              :customize-text="customizeShipmentText"
              width="20%"
            />
            <dx-column
              data-field="transport_kind"
              :caption="$t('transport_kind')"
              :customize-text="customizeTransportText"
              width="20%"
            />
            <dx-column
              data-field="description"
              :caption="$t('description')"
              width="30%"
            />
            <dx-editing
              v-if="!readOnly"
              :allow-deleting="true"
              :use-icons="true"
              mode="row"
            />
            <dx-state-storing
              :enabled="true"
              type="localStorage"
              :storage-key="`u${user.id}-service-freight-grid`"
            />
          </dx-data-grid>
        </div>

        <div class="form__group">
          <dx-fieldset-header
            id="route"
            :text="$t('Route')"
          />
          <b-row>
            <b-col lg="3">
              <dx-form-group :label="$t('Dispatch location')">
                <dx-autocomplete
                  v-model="freightOrder.dispatch_location"
                  :read-only="readOnly"
                  :min-search-length="0"
                  :max-length="100"
                  styling-mode="outlined"
                  :data-source="dispatch_location_datasource"
                  :validation-status="freightOrderErrors.dispatch_location ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.dispatch_location }"
                  @input="resetFreightOrderValidation('dispatch_location')"
                  @item-click="resetFreightOrderValidation('dispatch_location')"
                  @focusIn="onAutocompleteFocusIn($event, 'dispatch_location')"
                />
              </dx-form-group>
            </b-col>
            <b-col lg="3">
              <dx-form-group :label="$t('Loading port')">
                <dx-autocomplete
                  v-model="freightOrder.loading_port"
                  :read-only="readOnly"
                  :min-search-length="0"
                  :max-length="100"
                  styling-mode="outlined"
                  :data-source="loading_port_datasource"
                  :validation-status="freightOrderErrors.loading_port ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.loading_port }"
                  @input="resetFreightOrderValidation('loading_port')"
                  @item-click="resetFreightOrderValidation('loading_port')"
                  @focusIn="onAutocompleteFocusIn($event, 'loading_port')"
                />
              </dx-form-group>
            </b-col>
            <b-col lg="3">
              <dx-form-group :label="$t('Unloading port')">
                <dx-autocomplete
                  v-model="freightOrder.unloading_port"
                  :read-only="readOnly"
                  :min-search-length="0"
                  :max-length="100"
                  styling-mode="outlined"
                  :data-source="unloading_port_datasource"
                  :validation-status="freightOrderErrors.unloading_port ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.unloading_port }"
                  @input="resetFreightOrderValidation('unloading_port')"
                  @item-click="resetFreightOrderValidation('unloading_port')"
                  @focusIn="onAutocompleteFocusIn($event, 'unloading_port')"
                />
              </dx-form-group>
            </b-col>
            <b-col lg="3">
              <dx-form-group :label="$t('sea line')">
                <dx-autocomplete
                  v-model="freightOrder.sea_line"
                  :read-only="readOnly"
                  :min-search-length="0"
                  :max-length="100"
                  styling-mode="outlined"
                  :data-source="sea_line_datasource"
                  :validation-status="freightOrderErrors.sea_line ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.sea_line }"
                  @input="resetFreightOrderValidation('sea_line')"
                  @item-click="resetFreightOrderValidation('sea_line')"
                  @focusIn="onAutocompleteFocusIn($event, 'sea_line')"
                />
              </dx-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col lg="3">
              <dx-form-group :label="$t('Border crossing point')">
                <dx-select-box
                  v-model="freightOrder.border_crossing_point"
                  :read-only="readOnly"
                  :search-enabled="true"
                  :data-source="locations"
                  value-expr="id"
                  :display-expr="locationDisplayExpr"
                  styling-mode="outlined"
                  :validation-status="freightOrderErrors.border_crossing_point ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.border_crossing_point }"
                  @selection-changed="resetFreightOrderValidation('border_crossing_point')"
                />
              </dx-form-group>
            </b-col>
            <b-col
              lg="3"
              class="mb-4 mb-md-4 mb-lg-0"
            >
              <dx-form-group
                :label="$t('railway station from')"
                :margin-bottom="false"
                class="dx-form-group-select"
                :autocomplete="!readOnly"
                @select="showStationsModal('railway_station_from')"
              >
                <dx-autocomplete
                  v-model="freightOrder.railway_station_from"
                  :read-only="readOnly"
                  :min-search-length="0"
                  :max-length="100"
                  styling-mode="outlined"
                  :data-source="railway_station_from_datasource"
                  :validation-status="freightOrderErrors.railway_station_from ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.railway_station_from }"
                  @input="resetFreightOrderValidation('railway_station_from')"
                  @item-click="resetFreightOrderValidation('railway_station_from')"
                  @focusIn="onAutocompleteFocusIn($event, 'railway_station_from')"
                />
              </dx-form-group>
            </b-col>
            <b-col
              lg="3"
              class="mb-4 mb-md-4 mb-lg-0"
            >
              <dx-form-group
                :label="$t('railway station to')"
                :margin-bottom="false"
                class="dx-form-group-select"
                :autocomplete="!readOnly"
                @select="showStationsModal('railway_station_to')"
              >
                <dx-autocomplete
                  v-model="freightOrder.railway_station_to"
                  :read-only="readOnly"
                  :min-search-length="0"
                  :max-length="100"
                  styling-mode="outlined"
                  :data-source="railway_station_to_datasource"
                  :validation-status="freightOrderErrors.railway_station_to ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.railway_station_to }"
                  @input="resetFreightOrderValidation('railway_station_to')"
                  @item-click="resetFreightOrderValidation('railway_station_to')"
                  @focusIn="onAutocompleteFocusIn($event, 'railway_station_to')"
                />
              </dx-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col lg="3">
              <dx-form-group :label="$t('Destination')">
                <dx-autocomplete
                  v-model="freightOrder.destination"
                  :read-only="readOnly"
                  :min-search-length="0"
                  :max-length="100"
                  styling-mode="outlined"
                  :data-source="destination_datasource"
                  :validation-status="freightOrderErrors.destination ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.destination }"
                  @input="resetFreightOrderValidation('destination')"
                  @item-click="resetFreightOrderValidation('destination')"
                  @focusIn="onAutocompleteFocusIn($event, 'destination')"
                />
              </dx-form-group>
            </b-col>
            <b-col lg="9">
              <dx-form-group :label="$t('loading unloading address')">
                <dx-autocomplete
                  v-model="freightOrder.loading_unloading_address"
                  :read-only="readOnly"
                  :min-search-length="0"
                  :max-length="100"
                  styling-mode="outlined"
                  :data-source="loading_unloading_address_datasource"
                  :validation-status="freightOrderErrors.loading_unloading_address ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.loading_unloading_address }"
                  @input="resetFreightOrderValidation('loading_unloading_address')"
                  @item-click="resetFreightOrderValidation('loading_unloading_address')"
                  @focusIn="onAutocompleteFocusIn($event, 'loading_unloading_address')"
                />
              </dx-form-group>
            </b-col>
          </b-row>
        </div>

        <div class="form__group">
          <dx-fieldset-header
            id="contact-information"
            :text="$t('Contact Information')"
          />
          <b-row>
            <b-col
              v-for="it in contactInfoFields"
              :key="it.key"
              lg="4"
            >
              <dx-form-group :label="it.label">
                <dx-text-box
                  v-model="freightOrder[it.key]"
                  value-change-event="input"
                  styling-mode="outlined"
                  :max-length="it.maxLength"
                  :validation-status="freightOrderErrors[it.key] ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors[it.key] }"
                  @input="resetFreightOrderValidation(it.key)"
                />
              </dx-form-group>
            </b-col>
          </b-row>
        </div>

        <div
          v-if="user.is_manager"
          class="form__group"
        >
          <b-row
            class="d-flex flex-row justify-content-between align-items-center mb-3"
          >
            <b-col>
              <dx-fieldset-header
                id="expenses"
                :text="$t('Expenses')"
                class="mb-0"
              />
            </b-col>
          </b-row>

          <dx-data-grid
            v-if="freightOrder"
            ref="expensesGrid"
            class="mb-4"
            :remote-operations="remoteOperations"
            :data-source="freightOrderExpenses"
            :show-borders="false"
            :column-min-width="50"
            :allow-column-resizing="true"
            column-resizing-mode="widget"
            :hover-state-enabled="true"
            toolbar.visible="true"


            :allow-column-reordering="true"
            date-serialization-format="yyyy-MM-dd"
            @editor-preparing="onEditorPreparing"
          >
            <dx-column
              data-field="id"
              :visible="false"
            />
            <dx-column
              data-field="description"
              :caption="$t('description')"
              width="50%"
              :allow-editing="true"
            />
            <dx-column
              data-field="value"
              :caption="$t('Value')"
              width="15%"
              :allow-editing="true"
              data-type="number"
            />
            <dx-column
              data-field="vat"
              :caption="$t('Vat')"
              width="15%"
              :allow-editing="true"
              data-type="number"
            />
            <dx-column
              data-field="total"
              :caption="$t('Total')"
              width="15%"
              :allow-editing="true"
              data-type="number"
            />

            <dx-editing
              :allow-adding="user.is_manager"
              :allow-updating="user.is_manager"
              :allow-deleting="user.is_manager"
              :use-icons="true"
              mode="row"
            />

            <dx-state-storing
              :enabled="true"
              type="localStorage"
              :storage-key="`u${user.id}-expenses-freight-grid`"
            />

            <dx-selection
              show-check-boxes-mode="always"
              mode="single"
            />


            <dx-summary>
              <dx-total-item
                column="total"
                summary-type="sum"
                value-format="fixedPoint"
                :display-format="$t('Total')+': {0}'"
              />
            </dx-summary>
          </dx-data-grid>
        </div>


        <div
          v-if="isFreightOrderUpdate"
          class="form__group"
        >
          <dx-fieldset-header
            id="cost"
            :text="$t('Cost')"
          />
          <b-row class="group__cost mx-0">
            <b-col class="cost__panel pt-3">
              <b-row>
                <b-col>
                  <template v-if="user.is_manager">
                    <dx-form-group :label="$t('Approved cost')">
                      <dx-text-box
                        v-model="freightOrder.approved_cost"
                        value-change-event="input"
                        styling-mode="outlined"
                        :read-only="!managerCanApproveCost"
                        :validation-status="freightOrderErrors.approved_cost ? 'invalid' : 'valid'"
                        :validation-error="{ message: freightOrderErrors.approved_cost }"
                        @input="resetFreightOrderValidation('approved_cost')"
                      />
                    </dx-form-group>
                  </template>
                </b-col>
              </b-row>
              <b-row>
                <b-col>
                  <template v-if="user.is_manager">
                    <dx-form-group :label="$t('percent vat')">
                      <dx-text-box
                        v-model="freightOrder.vat_percent "
                        value-change-event="input"
                        styling-mode="outlined"
                        :read-only="!managerCanApproveCost"
                        :validation-status="freightOrderErrors.vat_percent ? 'invalid' : 'valid'"
                        :validation-error="{ message: freightOrderErrors.vat_percent }"
                        @input="resetFreightOrderValidation('vat_percent')"
                      />
                    </dx-form-group>
                  </template>
                </b-col>
              </b-row>

              <b-row v-if="user.is_manager">
                <b-col>
                  <dx-form-group :label="$t('Approved cost')">
                    <dx-text-area
                      v-model="freightOrder.approved_cost_description"
                      :read-only="!managerCanApproveCost"
                      :validation-status="freightOrderErrors.approved_cost_description ? 'invalid' : 'valid'"
                      :validation-error="{ message: freightOrderErrors.approved_cost_description }"
                      value-change-event="input"
                      styling-mode="outlined"
                      :max-length="1000"
                      @input="resetFreightOrderValidation('approved_cost_description')"
                    />
                  </dx-form-group>
                </b-col>
              </b-row>

              <b-row>
                <b-col>
                  <p
                    class="pt-3 mb-0 border-top"
                  >
                    {{ $t('Total:') }}
                  </p>
                  <h1 class="pt-0">
                    {{ finalPriceComputed }}
                  </h1>
                </b-col>
              </b-row>
            </b-col>

            <b-col class="cost__panel pt-3">
              <div class="cost__panel-header">
                <dx-fieldset-header
                  :text="$t('Included in the rate')"
                />
                <dx-button
                  :disabled="!managerCanApproveCost"
                  styling-mode="text"
                  @click="showModalCostPositionsShown(true)"
                >
                  <i class="ace-icon icon-edit" />
                </dx-button>
              </div>
              <ul
                class="cost__rate-list cost__included-list"
              >
                <li
                  v-for="item in costDetailsIncludes"
                  :key="item.description"
                >
                  <div class="rate-list__description">
                    {{ item.description }}
                  </div>
                  <div
                    v-if="item.body"
                    class="rate-list__body"
                  >
                    {{ item.body }}
                  </div>
                  <div
                    v-if="item.note"
                    class="rate-list__note"
                  >
                    {{ item.note }}
                  </div>
                </li>
              </ul>
            </b-col>
            <b-col
              class="cost__panel pt-3"
            >
              <div class="cost__panel-header">
                <dx-fieldset-header
                  :text="$t('Not included in the rate')"
                />
                <dx-button
                  styling-mode="text"
                  :disabled="!managerCanApproveCost"
                  @click="showModalCostPositionsShown(false)"
                >
                  <i class="ace-icon icon-edit" />
                </dx-button>
              </div>
              <ul
                class="cost__rate-list cost__not-included-list"
              >
                <li
                  v-for="item in costDetailsNotIncludes"
                  :key="item.description"
                >
                  <div
                    class="rate-list__description"
                  >
                    {{ item.description }}
                  </div>
                  <div
                    v-if="item.body"
                    class="rate-list__body"
                  >
                    {{ item.body }}
                  </div>
                  <div
                    v-if="item.note"
                    class="rate-list__note"
                  >
                    {{ item.note }}
                  </div>
                </li>
              </ul>
            </b-col>
          </b-row>
        </div>

        <div
          v-if="isFreightOrderUpdate"
          class="form__group"
        >
          <b-row class="align-items-baseline">
            <b-col cols="auto">
              <dx-fieldset-header
                v-if="isFreightOrderUpdate"
                id="status"
                :text="$t('Freight order status')"
                class="mb-0"
              />
            </b-col>
            <b-col cols="auto">
              <a
                href="#"
                class="history-order-link"
                @click.prevent="isHistoryShown = true"
              >
                {{ $t('Freight order history') }}
              </a>
            </b-col>
          </b-row>

          <b-row v-if="isFreightOrderUpdate">
            <b-col>
              <b-row>
                <b-col>
                  <dx-form-group>
                    <dx-radio-group
                      v-model="statusEdited"
                      :data-source="allowedStatuses"
                      value-expr="id"
                      :display-expr="it => it ? (freightOrder.status===it.id ? $t(it.name): $t(it.transition_name)) : $t('Not selected')"
                      styling-mode="outlined"
                      layout="horizontal"
                    />
                  </dx-form-group>
                </b-col>
              </b-row>

              <b-row v-if="isCommentRequired">
                <b-col>
                  <dx-form-group :label="$t('Status comment')">
                    <dx-text-area
                      ref="statusComment"
                      v-model="statusCommentEdited"
                      :read-only="!user.is_manager || statusEdited === freightOrder.status"
                      value-change-event="input"
                      styling-mode="outlined"
                      height="120"
                      :max-length="1000"
                    />
                  </dx-form-group>
                  <div
                    v-if="!statusCommentEdited"
                    class="status-validation text-danger"
                  >
                    <p class="pt-1">
                      {{ $t('The field is required') }}
                    </p>
                  </div>
                </b-col>
              </b-row>
              <b-row v-if="isAttachmentRequired && statusEdited !== freightOrder.status">
                <b-col>
                  <small
                    class="mb-2"
                  >
                    Шаг 1
                  </small>
                  <p>{{ $t('Please download print version of the document, print and sign.') }}</p>
                  <dx-button
                    v-if="isFreightOrderUpdate && isAllowedForPrint"
                    :text="$t('Download print version')"
                    icon="ace-icon icon-download"
                    type="success"
                    styling-mode="text"
                    class="custom-btn mr-2 mb-3"
                    @click="printToSign"
                  />
                </b-col>
                <b-col>
                  <small
                    class="mb-2"
                  >
                    Шаг 2
                  </small>
                  <p>{{ $t('Please scan the signed document and upload it here.') }}</p>
                  <dx-file-uploader
                    class="status__file-uploader"
                    upload-mode="useForm"
                    :on-value-changed="updateDocumentSigned"
                  />
                  <dx-button
                    :text="$t(isFreightOrderUpdate ? 'Save' : 'Create')"
                    type="success"
                    styling-mode="contained"
                    class="custom-btn mt-3"
                    width="100%"
                    :disabled="loading"
                    @click="onSubmit"
                  />
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </div>
        <b-row
          v-if="isDefaultSave"
          class="align-items-baseline mx-0 mb-3 mt-3"
        >
          <dx-button
            :text="$t(isFreightOrderUpdate ? 'Save' : 'Create')"
            type="success"
            styling-mode="contained"
            class="custom-btn mr-2"
            :disabled="loading"
            @click="onSubmit"
          />
        </b-row>
        <div
          v-if="isFreightOrderUpdate"
          class="form__group"
        >
          <dx-fieldset-header
            id="payment"
            :text="$t('Payment')"
          />
          <b-row class="align-items-center">
            <b-col
              lg="3"
              sm="5"
            >
              <dx-form-group :label="$t('Payment date')">
                <dx-date-box
                  v-model="freightOrder.paid_date"
                  date-serialization-format="yyyy-MM-dd"
                  type="date"
                  styling-mode="outlined"
                  :disabled="freightOrder.is_paid"
                  :validation-status="freightOrderErrors.paid_date ? 'invalid' : 'valid'"
                  :validation-error="{ message: freightOrderErrors.paid_date }"
                  @change="resetFreightOrderValidation('paid_date')"
                />
              </dx-form-group>
            </b-col>
            <b-col
              cols="auto"
              class="pr-0"
            >
              <dx-button
                :text="freightOrder.is_paid ? $t('Cancel payment') : $t('is_paid')"
                type="normal"
                class="cost__is-paid float-right"
                :disabled="!user.can_set_is_paid || (!freightOrder.paid_date && !freightOrder.is_paid)"
                @click="toogleIsPaid"
              />
            </b-col>
            <b-col cols="auto">
              <dx-check-box
                v-model="freightOrder.is_paid"
                :disabled="true"
              />
            </b-col>
          </b-row>
        </div>
      </form>

      <div
        v-if="isFreightOrderUpdate"
        class="form__group signed-documents"
      >
        <b-row class="align-items-baseline mb-3">
          <b-col
            cols="auto"
          >
            <dx-fieldset-header
              id="signed-documents"
              :text="$t('Signed documents')"
              class="mb-0"
            />
          </b-col>
          <b-col
            cols="auto"
            class="pl-lg-0"
          >
            <a
              href="#"
              class="history-signed-docs-link"
              @click.prevent="isSignedDocsShown = true"
            >
              {{ $t('Freight order history') }}
            </a>
          </b-col>
        </b-row>
        <b-row>
          <b-col>
            <dx-data-grid :data-source="revisionDocs">
              <dx-column
                :caption="$t('Date')"
                data-field="create_date"
                data-type="date"
              >
              </dx-column>
              <dx-column
                :caption="$t('Status')"
                cell-template="status-cell-template"
              />
              <template #status-cell-template="{ data }">
                <div class="cell-status">
                  {{ $t(data.data.for_status.name) }}
                </div>
              </template>
              <dx-column
                :caption="$t('User')"
                data-field="author.username"
              >
              </dx-column>
              <dx-column
                :caption="$t('Download')"
                cell-template="download-cell-template"
              />
              <template #download-cell-template="{ data }">
                <a
                  href="#"
                  @click.prevent="openSign(data.data)"
                >
                  {{ data.data.filename }}
                </a>
              </template>
            </dx-data-grid>
          </b-col>
        </b-row>
      </div>

      <div
        v-if="isFreightOrderUpdate"
        class="form__group"
      >
        <b-row>
          <b-col lg="12">
            <dx-fieldset-header
              id="documents"
              :text="$t('Documents')"
            />
            <dx-file-uploader
              accept="*"
              name="file"
              :multiple="true"
              :upload-url="uploadUrl"
              :upload-headers="{ authorization: `JWT ${token}`}"
              :on-uploaded="refreshFileTable"
              class="mb-4"
            />
            <dx-data-grid
              ref="fileGrid"
              :data-source="freightOrderFiles"
              :show-column-headers="true"
              :show-borders="false"
              date-serialization-format="yyyy-MM-dd"
            >
              <dx-column
                data-field="document_number"
                :caption="$t('freight_order.files.document_number')"
                :allow-search="false"
              />
              <dx-column
                data-field="document_date"
                :caption="$t('freight_order.files.document_date')"
                :allow-search="false"
                data-type="date"
              />
              <dx-column
                data-field="document_type"
                :caption="$t('freight_order.files.document_type')"
              >
                <dx-lookup
                  :data-source="documentTypeList"
                  value-expr="id"
                  display-expr="title"
                />
              </dx-column>
              <dx-column
                class="documents__column-origin"
                data-field="document_title"
                :caption="$t('freight_order.files.document_title')"
                :allow-search="false"
              />
              <dx-column
                data-field="is_original"
                :caption="$t('freight_order.files.is_original')"
                :allow-search="false"
              />
              <dx-column
                data-field="url"
                caption="URL"
                :allow-search="false"
                cell-template="file-cell-template"
                :allow-editing="false"
              />
              <dx-column
                data-field="create_date"
                :caption="$t('freight_order.files.create_date')"
                :allow-search="false"
                :allow-editing="false"
                data-type="date"
              />

              <dx-editing
                :allow-deleting="true"
                :allow-updating="true"
                :use-icons="true"
                mode="row"
              />
              <template #file-cell-template="{ data }">
                <a
                  href="#"
                  @click.prevent="openFile(data.data)"
                >
                  {{ data.data.filename }}
                </a>
              </template>
            </dx-data-grid>
          </b-col>
        </b-row>
      </div>

      <div
        v-if="isFreightOrderUpdate"
        class="form__group"
      >
        <b-row class="flex-row justify-content-between align-items-center mb-3">
          <b-col>
            <dx-fieldset-header
              id="containers"
              :text="$t('Containers')"
              class="mb-0"
            />
          </b-col>
          <b-col>
            <dx-button
              :text="$t('Add containers')"
              type="success"
              styling-mode="contained"
              class="custom-btn float-right"
              icon="ace-icon icon-plus"
              :disabled="loading"
              @click="showAddContainer"
            />
          </b-col>
        </b-row>
        <b-row>
          <b-col lg="12">
            <dx-data-grid
              ref="containerGrid"
              class="mb-4"
              toolbar.visible="true"
              :data-source="freightOrderContainers"
              :remote-operations="remoteOperations"
              :allow-column-reordering="true"
              :show-borders="false"
              :column-min-width="50"
              :allow-column-resizing="true"
              column-resizing-mode="widget"
              :hover-state-enabled="true"
              :on-row-dbl-click="onRowDblClick"
              date-serialization-format="yyyy-MM-dd"
              @selection-changed="validatePackageBtns"
              @content-ready="validateContainersAdding"
              @cell-prepared="onContainersCellPrepared"
              @toolbar-preparing="onToolbarPreparing"
              @editor-preparing="onEditorPreparing"
            >
              <dx-column
                data-field="container"
                :caption="$t('Container')"
                :header-filter="{ searchEnabled: true}"
                :editor-options="{ maxLength: 15 }"
              />

              <dx-column
                data-field="pkg_state"
                :caption="$t('Package state')"
                :lookup="packageStatusLookup"
                :allow-search="false"
                :allow-editing="false"
              />

              <dx-column
                data-field="port_freight_forwarding_status"
                :caption="$t('port freight forwarding status')"
                :allow-search="false"
                :allow-editing="false"
                data-type="string"
              />

              <dx-column
                data-field="port_freight_forwarding_date"
                :caption="$t('port freight forwarding status date')"
                :allow-search="false"
                :allow-editing="false"
                data-type="date"
              />

              <dx-column
                data-field="transit_decl_status"
                :caption="$t('transit decl status')"
                :allow-search="false"
                :allow-editing="false"
                data-type="string"
              />

              <dx-column
                data-field="transit_decl_date"
                :caption="$t('transit decl status date')"
                :allow-search="false"
                :allow-editing="false"
                data-type="date"
              />
              <template #pkg-pid-cell-template="{ data }">
                <a>
                  {{ data.value }}
                </a>
              </template>

              <dx-search-panel
                :visible="true"
                :highlight-case-sensitive="false"
              />

              <dx-editing
                :allow-updating="!readOnlyContainers"
                :allow-deleting="!readOnlyContainers"
                :allow-adding="containersAddingAllowed && !readOnlyContainers"
                :use-icons="true"
                mode="row"
              />

              <dx-header-filter :visible="true" />
              <dx-export
                :enabled="true"
                :allow-export-selected-data="true"
                file-name="containers"
              />
              <dx-selection
                show-check-boxes-mode="always"
                mode="single"
              />
              <dx-paging :page-size="15" />
              <dx-pager
                :show-page-size-selector="true"
                :allowed-page-sizes="[10, 20, 50]"
              />

              <dx-state-storing
                :enabled="true"
                type="localStorage"
                :storage-key="`u${user.id}-freight-order-companies-grid-storage`"
              />

              <template #createPackage="{}">
                <dx-button
                  :text="$t('Create package')"
                  type="success"
                  styling-mode="text"
                  class="custom-btn mr-2"
                  :disabled="loading"
                  icon="ace-icon icon-bind"
                  @click="showPackageContent(false)"
                />
              </template>
            </dx-data-grid>

            <dx-button
              :text="$t('Withdraw package')"
              type="success"
              styling-mode="contained"
              class="custom-btn mr-2"
              :disabled="loading || !packageBtnsValid"
              @click="withdrawPackage"
            />
          </b-col>
        </b-row>
      </div>

      <dx-popup
        v-if="id"
        :key="freightOrderHistoryKey"
        :visible.sync="isHistoryShown"
        :drag-enabled="false"
        :hide-on-outside-click="true"
        :show-title="true"
        :height="500"
        :title="$t('Freight order history')"
      >
        <dx-scroll-view>
          <freight-order-history-modal :id="id" />
        </dx-scroll-view>
      </dx-popup>
      <dx-popup
        v-if="containerPopupVisible"
        :visible.sync="containerPopupVisible"
        :drag-enabled="false"
        :close-on-outside-click="true"
        :show-title="true"
        :width="800"
        :height="500"
        :title="$t('Container') + ': ' + currentContainer.container"
      >
        <dx-scroll-view>
          <b class="d-block mb-3">
            {{ $t('Border crossing point') }}
          </b>
          <dx-select-box
            v-model="freightOrder.border_crossing_point"
            :search-enabled="true"
            :data-source="locations"
            value-expr="id"
            :display-expr="locationDisplayExpr"
            styling-mode="outlined"
            class="mb-3"
          />
          <b class="d-block mb-3">
            {{ $t('Select a files related to the selected container') }}
          </b>
          <dx-data-grid
            ref="fileGrid"
            :data-source="freightOrderFiles"
            :selected-row-keys="selectedRowKeys"
            :remote-operations="remoteOperations"
            :allow-column-resizing="true"
            class="mb-3"
          >
            <dx-column
              data-field="filename"
              :caption="$t('file name')"
              :allow-search="false"
              cell-template="file-cell-template"
            />
            <dx-column
              data-field="document_number"
              :caption="$t('freight_order.files.document_number')"
              :allow-search="false"
            />
            <dx-column
              data-field="document_date"
              :caption="$t('freight_order.files.document_date')"
              :allow-search="false"
              data-type="date"
            />
            <dx-column
              data-field="document_type"
              :caption="$t('freight_order.files.document_type')"
            >
              <dx-lookup
                :data-source="documentTypeList"
                value-expr="id"
                display-expr="title"
              />
            </dx-column>

            <template #file-cell-template="{ data }">
              <a
                href="#"
                @click.prevent="openFile(data.data)"
              >
                {{ data.data.filename }}
              </a>
            </template>
            <dx-selection
              show-check-boxes-mode="always"
              mode="multiple"
            />
          </dx-data-grid>
          <b-row class="mx-0">
            <dx-button
              :text="$t('Save')"
              type="success"
              styling-mode="contained"
              class="custom-btn mr-2 ml-auto"
              :disabled="loading || !freightOrder.border_crossing_point"
              @click="updateContainers"
            />
            <dx-button
              :text="$t('Cancel')"
              type="success"
              styling-mode="text"
              class="custom-btn"
              :disabled="loading"
              @click="containerPopupVisible = false"
            />
          </b-row>
        </dx-scroll-view>
      </dx-popup>
      <consigment-info-modal
        v-if="isConsigmentInfoModalShown"
        v-model="isConsigmentInfoModalShown"
        :mode="consigmentModalMode"
        :countries="countries"
        :country-display-expr="countryDisplayExpr"
        :companies="companies"
        :company-display-expr="companyDisplayExpr"
        @update="updateConsigmentInfoFromModal"
      />
      <stations-info-modal
        v-if="isStationsInfoModalShown"
        v-model="isStationsInfoModalShown"
        @update="updateStationsInfoFromModal"
      />


      <autocomplete-modal
        v-if="isAutocompleteModalShown"
        v-model="isAutocompleteModalShown"
        :field="autocompleteModalField"
        @selected="updateTextarea"
      />
      <service-modal
        v-model="isServiceModalShown"
        :selected-services="freightOrder.ordered_services"
        :service-shipment-types="serviceShipmentTypes"
        :service-transport-types="serviceTransportTypes"
        :services="servicesNotSelected"
        @submit="addFreightOrderedServices"
      />
      <!-- <freight-order-history-modal
          v-if="isFreightOrderHistoryShown"
          v-model="isFreightOrderHistoryShown"
        /> -->
      <cost-positions-modal
        :id="freightOrder.id"
        v-model="isCostPositionsShown"
        :cost-details-includes="costDetailsIncludes"
        :cost-details-not-includes="costDetailsNotIncludes"
        :tab-include="tabInclude"
        :default-cost-details="defaultCostDetails"
        @refresh-cost-positions="costDetailsInfo"
        @refresh-tab="tabInclude = $event"
      />
      <signed-docs-modal
        v-if="isSignedDocsShown"
        v-model="isSignedDocsShown"
        :signed-docs="signedDocs"
      />
      <add-container-modal
        v-if="isAddContainerShown"
        v-model="isAddContainerShown"
        :left-containers="leftContainers"
        @submit="addContainer"
      />
      <describe-document-modal />
    </div>
    <right-sidebar
      :show-sidebar="showSidebar"
    />
  </main>
</template>

<script>
import { mapState } from 'vuex';


import {
  DxSelectBox,
  DxTextBox,
  DxNumberBox,
  DxCheckBox,
  DxDateBox,
  DxTextArea,
  DxFileUploader,
  DxButton,
} from 'devextreme-vue';
import { DxButton as DxTextBoxButton } from 'devextreme-vue/text-box';
import { DxAutocomplete } from 'devextreme-vue/autocomplete';
import { DxPopup } from 'devextreme-vue/popup';
import { DxScrollView } from 'devextreme-vue/scroll-view';
import { DxRadioGroup } from 'devextreme-vue/radio-group';

import {
  DxDataGrid,
  DxExport,
  DxColumn,
  DxEditing,
  DxHeaderFilter,
  DxSearchPanel,
  DxSelection,
  DxStateStoring,
  DxSummary,
  DxTotalItem,
  DxPaging,
  DxPager,
  DxLookup,
} from 'devextreme-vue/data-grid';


import ruMessages from 'devextreme/localization/messages/ru.json';
import { locale as localize, loadMessages } from 'devextreme/localization';
import LeftSidebar from '../components/freight-order/FreightOrderBlocks/LeftSidebar.vue';
import RightSidebar from '../components/freight-order/FreightOrderBlocks/RightSidebar.vue';

import DxFormGroup from '@/components/common/DxFormGroup';
import DxFieldsetHeader from '@/components/common/DxFieldsetHeader';
// import FreightOrderHistory from '@/components/freight-order/FreightOrderHistory';
import FreightOrderHistoryModal from '@/components/freight-order/FreightOrderHistoryModal';
import DescribeDocumentModal from '@/components/freight-order/DescribeDocumentModal';

import createCustomStore from '@/utils/create-custom-store';
import createStatusLookup from '@/utils/create-status-lookup';
import createPackageStatusLookup from '@/utils/create-package-status-lookup';

import ConsigmentInfoModal from '@/components/freight-order/ConsigmentInfoModal.vue';
import StationsInfoModal from '@/components/freight-order/StationsInfoModal.vue';
import AutocompleteModal from '@/components/freight-order/AutocompleteModal.vue';
import ServiceModal from '@/components/freight-order/ServiceModal.vue';
import AddContainerModal from '@/components/freight-order/AddContainerModal.vue';
import validateContainerNumber from '@/utils/container-number-validator';
import SignedDocsModal from '@/components/freight-order/SignedDocsModal.vue';
import CostPositionsModal from '@/components/freight-order/CostPositionsModal.vue';

const contentDisposition = require('content-disposition');

const mapAutocompleteDataSources = fields => {
  return fields.reduce((obj, field) => {
    const model = `${field}_datasource`;
    const computed = () => {
      return {
        store: createCustomStore(`tracking/api/directory/freight-order-suggestion?field_name=${field}`),
        paginate: false,
      };
    };
    obj[model] = computed;
    return obj;
  }, {});
};


export default {
  name: 'FreightOrder',
  components: {
    DxAutocomplete,
    DxSelectBox,
    DxTextBox,
    DxNumberBox,
    DxDateBox,
    DxTextArea,
    DxCheckBox,
    DxFormGroup,
    DxFieldsetHeader,
    DxDataGrid,
    DxExport,
    DxColumn,
    DxLookup,
    // DxList,
    DxEditing,
    DxHeaderFilter,
    // DxGroupPanel,
    DxSearchPanel,
    DxSelection,
    DxStateStoring,
    DxSummary,
    DxTotalItem,
    DxFileUploader,
    DxPaging,
    DxPager,
    DxButton,
    DxPopup,
    DxScrollView,
    DxRadioGroup,
    // FreightOrderHistory,
    FreightOrderHistoryModal,
    ConsigmentInfoModal,
    StationsInfoModal,
    AutocompleteModal,
    DxTextBoxButton,
    ServiceModal,
    SignedDocsModal,
    AddContainerModal,
    DescribeDocumentModal,
    CostPositionsModal,
    LeftSidebar,
    RightSidebar,
  },
  props: {
    id: String,
  },
  data: vue => ({
    services: [],
    isConsigmentInfoModalShown: false,
    isAutocompleteModalShown: false,
    isServiceModalShown: false,
    isFreightOrderHistoryShown: false,
    isAddContainerShown: false,
    isHistoryShown: false,
    isCostPositionsShown: false,
    showSidebar: true,
    tabInclude: true,

    autocompleteModalField: null,
    consigmentModalMode: 'consignee',
    consignorButton: {
      icon: 'search',
      type: 'normal',
      onClick: () => {
        vue.isConsigmentInfoModalShown = true;
        vue.consigmentModalMode = 'consignor';
      },
    },
    consigneeButton: {
      icon: 'search',
      type: 'normal',
      onClick: () => {
        vue.isConsigmentInfoModalShown = true;
        vue.consigmentModalMode = 'consignee';
      },
    },
    isStationsInfoModalShown: false,
    stationsInfoModalField: '',
    dropDownOptions: {
      width: '50vw',
    },

    createCustomStore,
    loading: false,
    freightOrderHistoryKey: 0,
    isSignedDocsShown: false,
    currentContainer: null,
    containerPopupVisible: false,
    selectedRowKeys: [],
    leftContainers: 0,
    freightOrder: {
      approved_cost: null,
      approved_cost_description: '',
      vat_percent: null,
      freight_cost_currency: 'RUB',
      border_crossing_point: null,
      ordered_services: [],
    },
    freightOrderErrors: {},
    statusEdited: null,
    statusCommentEdited: null,
    currentStatus: {
      name: '',
    },
    totalPrice: 0,
    containers: null,
    companies: null,
    refbookETSNG: null,
    refbookGNG: null,
    countries: null,
    locations: null,
    freightOrderFiles: null,
    freightOrderContainers: null,
    freightOrderExpenses: null,
    remoteOperations: {
      sorting: true,
      filtering: true,
      paging: true,
    },
    documentTypeList: createCustomStore('tracking/api/document-types/'),
    statusLookup: createStatusLookup(vue),
    packageStatusLookup: createPackageStatusLookup(vue),
    apiUrl: process.env.VUE_APP_API_URL,
    token: localStorage.getItem('access-token'),
    railway_service: null,
    statusList: [],
    allowedStatuses: [],
    isConsignorCollapseShown: false,
    defaultImportInfo: {
      consignor: 'ООО "ВОСТОЧНАЯ СТИВИДОРНАЯ КОМПАНИЯ"',
      consignor_postalcode: '692941',
      consignor_countrycode: 'RU',
      consignor_country_name: 'РОССИЯ',
      consignor_city: 'НАХОДКА ВРАНГЕЛЬ-1',
      consignor_streethouse: 'УЛ.ВНУТРИПОРТОВАЯ 14',
      consignor_okpo: '73262854',
      consignor_rail_code: '6856',
    },
    defaultExportInfo: {
      consignee: 'ООО "ВОСТОЧНАЯ СТИВИДОРНАЯ КОМПАНИЯ"',
      consignee_postalcode: '692941',
      consignee_countrycode: 'RU',
      consignee_country_name: 'РОССИЯ',
      consignee_city: 'НАХОДКА ВРАНГЕЛЬ-1',
      consignee_streethouse: 'УЛ.ВНУТРИПОРТОВАЯ 14',
      consignee_okpo: '73262854',
      consignee_rail_code: '6856',
    },
    documentSigned: null,
    packageBtnsValid: false,
    containersAddingAllowed: false,
    transportKinds: null,
    containerTypes: null,
    transport_kinds: null,
    shipmentKinds: null,
    serviceShipmentTypes: [],
    serviceTransportTypes: [],
    classesForStatuses: {
      new: 'new-status',
      editing: 'new-status',
      fo_agreein: 'pending-status',
      apprv_age: 'pending-status',
      cost_agreeing: 'apprv-cost-status',
      cost_calculation: 'apprv-cost-status',
      rejected: 'canceled-status',
      canceled: 'canceled-status',
      agreed: 'approved-status',
      forwarding: 'approved-status',
      in_work: 'approved-status',
      done: 'archive-status',
      archive: 'archive-status',
    },
    signedDocs: [],
    revisionDocs: [],
    orderCostDetails: [],
    defaultCostDetails: [],
  }),
  computed: {
    ...mapState(['user']),
    ...mapAutocompleteDataSources([
      'delivery_terms',
      'forwarder_in_port',
      'notify',
      'dispatch_location',
      'destination',
      'loading_unloading_address',
      'packing_kind',
      'direction',
      'sea_line',
      'loading_port',
      'unloading_port',
      'railway_station_from',
      'railway_station_to',
    ]),
    documentNavigation() {
      return [
        {
          href: '#order',
          text: 'Order',
          show: true,
        },
        {
          href: '#consignor-consignee',
          text: 'Consignor / Consignee',
          show: true,
        },
        {
          href: '#fright-details',
          text: 'Freight details',
          show: true,
        },
        {
          href: '#shipping-period',
          text: 'Shipping period and containers',
          show: true,
        },
        {
          href: '#services',
          text: 'Services',
          show: true,
        },
        {
          href: '#route',
          text: 'Route',
          show: true,
        },
        {
          href: '#contact-information',
          text: 'Contact Information',
          show: true,
        },
        {
          href: '#expenses',
          text: 'Expenses',
          show: this.isFreightOrderUpdate && this.user.is_manager,
        },
        {
          href: '#cost',
          text: 'Cost',
          show: this.isFreightOrderUpdate,
        },
        {
          href: '#status',
          text: 'Freight order status',
          show: this.isFreightOrderUpdate,
        },
        {
          href: '#payment',
          text: 'Payment',
          show: this.isFreightOrderUpdate,
        },
        {
          href: '#signed-documents',
          text: 'Signed documents',
          show: this.isFreightOrderUpdate,
        },
        {
          href: '#documents',
          text: 'Documents',
          show: this.isFreightOrderUpdate,
        },
        {
          href: '#containers',
          text: 'Containers',
          show: this.isFreightOrderUpdate,
        },
      ];
    },
    costDetailsIncludes() {
      return this.orderCostDetails.filter(it => it.included);
    },
    costDetailsNotIncludes() {
      return this.orderCostDetails.filter(it => !it.included);
    },
    isFreightOrderUpdate() {
      return !!this.$route.params.id;
    },
    statusUserCanEdit() {
      return this.statusList.find(it => it.user_can_edit && it.id === this.freightOrder.status);
    },
    statusUserCanEditContainers() {
      return this.statusList.find(it => it.user_can_edit_containers && it.id === this.freightOrder.status);
    },
    readOnly() {
      const newOrder = !this.isFreightOrderUpdate;
      return !this.user.is_manager && !this.statusUserCanEdit && !newOrder;
    },
    readOnlyContainers() {
      const newOrder = !this.isFreightOrderUpdate;
      return !this.user.is_manager && !this.statusUserCanEditContainers && !newOrder;
    },
    isAllowedForPrint() {
      return this.user.is_manager || this.statusList.find(it => it.user_can_print && it.id === this.freightOrder.status);
    },
    isCommentRequired() {
      const statusEdited = this.allowedStatuses.find(it => it.id === this.statusEdited);
      return statusEdited ? statusEdited.comment_required : false;
    },
    isAttachmentRequired() {
      const statusEdited = this.allowedStatuses.find(it => it.id === this.statusEdited);
      return statusEdited ? statusEdited.attachment_required : false;
    },
    isDefaultSave() {
      return (this.statusEdited === this.freightOrder.status || !this.isAttachmentRequired);
    },
    managerCanApproveCost() {
      const freightOrderStatus = this.allowedStatuses.find(it => it.id === this.freightOrder.status);
      return this.user.is_manager && (freightOrderStatus ? freightOrderStatus.manager_can_approve_cost : false);
    },
    submitBtnDisabled() {
      return this.loading || !this.freightOrder.border_crossing_point;
    },
    pageTitle() {
      return this.isFreightOrderUpdate
        ? `${this.$t('Shipping order no')} ${this.id}.`
        : this.$t('Shipping order');
    },
    contactInfoFields() {
      return [
        {
          key: 'order_contacts',
          label: this.$t('Order contacts'),
          maxLength: 100,
        },
        {
          key: 'order_contacts_email',
          label: this.$t('Email'),
          maxLength: 100,
        },
        {
          key: 'order_contacts_phone',
          label: this.$t('Phone'),
          maxLength: 100,
        },
      ];
    },
    consigmentFields() {
      return [
        {
          key: 'okpo',
          label: this.$t('OKPO'),
          maxLength: 10,
          lg: 6,
        },
        {
          key: 'inn',
          label: this.$t('INN'),
          maxLength: 12,
          lg: 6,
        },
        {
          key: 'rail_code',
          label: this.$t('Railway code'),
          maxLength: 18,
          lg: 6,
        },
        {
          key: 'countrycode',
          label: this.$t('Country'),
          lg: 6,
        },
        {
          key: 'city',
          label: this.$t('City'),
          maxLength: 35,
        },
        {
          key: 'postalcode',
          label: this.$t('Postal code'),
          maxLength: 9,
          lg: 6,
        },
        {
          key: 'streethouse',
          label: this.$t('Address'),
          maxLength: 50,
          lg: 12,
        },
        {
          key: 'contact_person',
          label: this.$t('Contact person'),
          maxLength: 100,
          lg: 12,
        },
        {
          key: 'phone',
          label: this.$t('Phone'),
          maxLength: 100,
          lg: 6,
        },
        {
          key: 'email',
          label: this.$t('Email'),
          maxLength: 50,
          lg: 6,
        },
      ];
    },
    uploadUrl(vue) {
      return `${process.env.VUE_APP_API_URL}/tracking/api/freight_orders/${vue.id}/files/`;
    },
    finalPriceComputed() {
      if ((this.freightOrder.approved_cost_description || '').length > 0) {
        return this.freightOrder.approved_cost_description;
      }
      if (this.freightOrder.approved_cost) {
        let ret = `${this.freightOrder.approved_cost} RUB.`;
        ret += (this.freightOrder.vat_percent ? ` В том числе НДС ${this.freightOrder.vat_percent}%.` : '');
        if (this.freightOrder.container_count > 0) {
          ret += ` За контейнер ${this.freightOrder.approved_cost / this.freightOrder.container_count} RUB.`;
        }
        return ret;
      }
      return 0;
    },
    servicesUrl() {
      const url = 'service/api/service/';
      let str = '';

      if ((this.freightOrder.transport_kinds && this.freightOrder.transport_kinds.length) || this.freightOrder.shipment_kind) {
        str += '?';

        if (this.freightOrder.transport_kinds.length) {
          str += `transport_kind=${this.freightOrder.transport_kinds.join('&transport_kind=')}`;

          if (this.freightOrder.shipment_kind) {
            str += `&shipment_kind=${this.freightOrder.shipment_kind}`;
          }
        } else if (this.freightOrder.shipment_kind) {
          str += `shipment_kind=${this.freightOrder.shipment_kind}`;
        }

        return url + str;
      }

      return url;
    },
    servicesSelected() {
      return this.services.filter(({ id }) => this.freightOrder.ordered_services.includes(id));
    },
    servicesNotSelected() {
      return this.services.filter(({ id }) => !this.freightOrder.ordered_services.includes(id));
    },
  },
  watch: {
    servicesUrl() {
      this.getServices();
    },
  },
  async created() {
    loadMessages(ruMessages);
    localize(this.$i18n.locale);

    this.getServiceShipmentTypes();
    this.getServiceTransportTypes();
    this.getServices();

    if (!this.isFreightOrderUpdate) {
      this.updateContactInfo();

      if (this.$route.query.copiedId) {
        await this.getFreightOrder();
      }
      return;
    }

    await this.getFreightOrder();
    this.getSignedDocs();
    this.revisionSignedDocs();
    this.costDetailsInfo();
    this.defaultCostDetailsInfo();
  },
  mounted() {
    this.companies = {
      store: createCustomStore('profile/api/companies/'),
      paginate: true,
    };
    this.refbookETSNG = {
      store: createCustomStore('refbooks/api/etsng/'),
      paginate: true,
    };
    this.refbookGNG = {
      store: createCustomStore('refbooks/api/gng/'),
      paginate: true,
    };
    this.countries = {
      store: createCustomStore('refbooks/api/country/'),
      paginate: true,
    };
    this.locations = {
      store: createCustomStore('tracking/api/locations/'),
      paginate: true,
    };
    this.routes = {
      store: createCustomStore('planner/api/railway_route/'),
      paginate: true,
    };
    this.shipmentKinds = {
      store: createCustomStore('service/api/shipment_kind/'),
      paginate: true,
    };
    this.transportKinds = {
      store: createCustomStore('service/api/transport_kind/'),
      paginate: true,
    };
    this.containerTypes = {
      store: createCustomStore('service/api/container_type/'),
      paginate: true,
    };
    this.showSidebar = JSON.parse(localStorage.getItem('sidebar'));

    this.freightOrderFiles = createCustomStore(`tracking/api/freight_orders/${this.$route.params.id}/files/`);
    this.freightOrderContainers = createCustomStore(`tracking/api/freight_orders/${this.$route.params.id}/containers/`);
    this.freightOrderExpenses = createCustomStore(`tracking/api/freight_orders/${this.$route.params.id}/expenses/`);
    this.freightOrderContainerPackageContent = null;

    if (this.$route.query.company) {
      this.freightOrder.company = +this.$route.query.company;
    }
  },
  methods: {
    // refreshTab(val) {
    //   console.log('refreshTab', val);
    //   this.tabInclude = val;
    // },
    sidebarLocale() {
      this.showSidebar = !this.showSidebar;
      localStorage.setItem('sidebar', this.showSidebar);
    },
    showModalCostPositionsShown(val) {
      this.isCostPositionsShown = true;
      this.tabInclude = val;
    },
    updateFreightOrderDescription(type, { selectedItem }) {
      if (!selectedItem) {
        return;
      }

      if (!this.freightOrder.freight_description) {
        this.freightOrder.freight_description = '';
      }

      const description = this.refBookDisplayExpr(selectedItem);
      if (this.freightOrder.freight_description.includes(`${this.$t(type)}:`)) {
        const regex = new RegExp(`${this.$t(type)}: .*`, 'gmi');
        this.freightOrder.freight_description = this.freightOrder.freight_description.replace(regex, `${this.$t(type)}: ${description}`);
        return;
      }

      this.freightOrder.freight_description = `${this.$t(type)}: ${description}\n${this.freightOrder.freight_description}`;
    },
    onToolbarPreparing(e) {
      e.toolbarOptions.items.push({
        location: 'before',
        widget: 'dxButton',
        options: {
          icon: 'ace-icon icon-refresh',
          onClick: this.refreshDataGrid.bind(this),
        },
      },
      {
        location: 'after',
        widget: 'dxButton',
        template: 'createPackage',
      });
      e.toolbarOptions.items.forEach((item) => {
        if (item.name === 'searchPanel') {
          item.location = 'before';
        }
      });
    },
    onEditorPreparing(e) {
      if (e.parentType === 'searchPanel') {
        e.editorOptions.stylingMode = 'outlined';
      }
    },

    refreshDataGrid() {
      this.$refs.containerGrid.instance.refresh();
    },
    clearFilter() {
      this.$refs.containerGrid.instance.clearFilter();
    },
    async getAllStatuses() {
      const { results } = await this.$http.get('tracking/api/freight-order-statuses/');
      this.statusList = results;
    },
    async getFreightOrder() {
      const id = this.$route.params.id || this.$route.query.copiedId;
      this.freightOrder = await this.$http.get(`tracking/api/freight_orders/${id}/`);

      if (!this.$route.query.copiedId) {
        this.getAllStatuses();
        this.getAllowedStatuses();
      }

      return Promise.resolve();
    },
    async onSubmit() {
      if (this.isFreightOrderUpdate) {
        await this.editFreightOrder();
        this.revisionSignedDocs();
        return;
      }

      this.addFreightOrder();
    },
    async editFreightOrder() {
      if (this.isCommentRequired && !this.statusCommentEdited) {
        this.$refs.statusComment.instance.focus();
        return;
      }

      if (this.isAttachmentRequired && !this.documentSigned) {
        return;
      }

      this.loading = true;
      let signFileId;

      if (this.isAttachmentRequired) {
        const formData = new FormData();
        formData.append('file', this.documentSigned);

        const { id } = await this.$http.post(`/tracking/api/freight_orders/${this.id}/sign/`, formData);
        signFileId = id;
      }

      try {
        await this.$http.patch(`tracking/api/freight_orders/${this.id}/`, {
          ...this.freightOrder,
          status: this.statusEdited,
          status_comment: this.statusCommentEdited,
          sign_file_id: signFileId,
        });
        await this.getFreightOrder();
        this.freightOrderHistoryKey++;
        this.freightOrderErrors = {};
        return Promise.resolve();
      } catch (e) {
        if (e.response.data) {
          this.freightOrderErrors = Array.from(Object.entries(e.response.data))
            .reduce((acc, [key, value]) => {
              acc[key] = value.map(it => this.$t(it));
              return acc;
            }, {});
        }
      } finally {
        this.loading = false;
      }
    },
    async addFreightOrder() {
      if (this.$route.query.copiedId) {
        delete this.freightOrder.id;
      }

      const { id } = await this.$http.post('tracking/api/freight_orders/', this.freightOrder);
      this.$router.push(`/freight-orders/${id}`);
    },
    getFileUrl(file) {
      return `${process.env.VUE_APP_API_URL}/tracking/api/freight_orders/${file.freight_order}/file/${file.id}`;
    },
    getSignUrl(file) {
      return `${process.env.VUE_APP_API_URL}/tracking/api/freight_orders/${file.freight_order}/sign/${file.id}`;
    },
    onRowDblClick(e) {
      if (!e.data.id) {
        return;
      }
      const q = `/container_tracking/api/container/?freight_order=${this.freightOrder.id}&container=${e.data.container}`;
      const thisRouter = this.$router;
      const containerId = e.data.id;
      this.$http.get(q)
        .then(r => {
          if (r.results.length > 0) {
            thisRouter.push(`/tracking/container/${r.results[0].id}`);
          } else {
            thisRouter.push(`/containers/${containerId}`);
          }
        });
    },
    companyDisplayExpr(company) {
      return company ? `${company.name} ${company.inn || ''}` : '';
    },
    refBookDisplayExpr(item) {
      return item ? `${item.code} ${item.description}` : '';
    },
    locationDisplayExpr(location) {
      return location ? location.location : '';
    },
    countryDisplayExpr(country) {
      const nameField = this.$i18n.locale === 'ru' ? 'short_name' : 'name_eng';
      return country ? `${country.abc2} ${country[nameField]}` : '';
    },
    refreshFileTable() {
      this.$refs.fileGrid.instance.refresh();
    },
    refreshContainerTable() {
      this.$refs.containerGrid.instance.refresh();
    },
    async showAddContainer() {
      this.isAddContainerShown = true;
    },
    async addContainer(containers) {
      await this.$http.post(`/tracking/api/freight_orders/${this.freightOrder.id}/containers/multi/`, {
        numbers: containers
          .map(i => i.number),
      }).then(() => { this.refreshContainerTable(); });
    },
    getCurrentContainer() {
      if (this.$refs.containerGrid.instance.getSelectedRowsData().length === 0) {
        alert(this.$t('Please select container'));
        return null;
      }

      return this.$refs.containerGrid.instance.getSelectedRowsData()[0];
    },
    async showPackageContent(isEdit) {
      this.currentContainer = this.getCurrentContainer();

      if (!this.currentContainer) {
        return;
      }

      if (!this.currentContainer.pkg_pid && isEdit) {
        alert(this.$t('There is no package for this container'));
        return;
      }

      const url = `tracking/api/freight_orders/${this.$route.params.id}/containers/${this.currentContainer.id}/package-content/`;
      const response = await createCustomStore(url)
        .load();

      this.selectedRowKeys = response.data.map(it => it.freight_order_file);
      this.containerPopupVisible = true;
    },

    async updateContainers() {
      const items = this.$refs.fileGrid.instance.getSelectedRowKeys()
        .map(it => ({ freight_order_file: it }));
      await this.freightOrderContainers.update(this.currentContainer.id, { package_content: items });
      await this.$http.patch(`tracking/api/freight_orders/${this.id}/`, {
        border_crossing_point: this.freightOrder.border_crossing_point,
      });

      this.containerPopupVisible = false;
      this.$refs.containerGrid.instance.refresh();
      this.$refs.containerGrid.instance.clearSelection();
    },
    withdrawPackage() {
      this.currentContainer = this.getCurrentContainer();

      if (!this.currentContainer) {
        return;
      }

      if (!this.currentContainer.pkg_pid) {
        alert(this.$t('There is no package for this container'));
        return;
      }

      /* eslint-disable */

        if (confirm(this.$t('Are you sure to withdraw the package?'))) {
          this.freightOrderContainers
            .update(this.currentContainer.id, {
              withdraw: true,
            })
            .then(() => {
              this.$refs.containerGrid.instance.refresh();
              this.$refs.containerGrid.instance.clearSelection();
            });
        }

        /* eslint-enable */
    },
    openFile(data) {
      this.$http({
        url: this.getFileUrl(data),
        method: 'GET',
        responseType: 'blob',
      })
        .then(response => {
          const href = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = href;
          link.setAttribute('download', data.filename);
          link.setAttribute('target', '_blank');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        });
    },

    openSign(data) {
      this.$http({
        url: this.getSignUrl(data),
        method: 'GET',
        responseType: 'blob',
      })
        .then(response => {
          const href = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement('a');
          link.href = href;
          link.setAttribute('download', data.filename);
          link.setAttribute('target', '_blank');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        });
    },

    async getAllowedStatuses() {
      const { results } = await this.$http.get(`tracking/api/freight_orders/${this.id}/wf/?ordering=order`);
      this.allowedStatuses = results;
      this.currentStatus = this.allowedStatuses.find(it => it.id === this.freightOrder.status);
      this.statusCommentEdited = this.freightOrder.status_comment;
      this.statusEdited = this.freightOrder.status;
    },
    updateContactInfo() {
      this.freightOrder.order_contacts_phone = this.user.phone;
      this.freightOrder.order_contacts_email = this.user.email;
      this.freightOrder.order_contacts = (this.user.first_name ? this.user.first_name + ' ' : '') + this.user.last_name || '';
    },
    async print() {
      await this.editFreightOrder();
      this.downloadFile(false);
    },
    async printToSign() {
      await this.editFreightOrder();
      this.downloadFileForStatus(this.statusEdited);
    },
    downloadFile(isSign) {
      const printUrl = this.apiUrl + `/tracking/api/freight_orders/${this.id}/print`;
      const signUrl = this.apiUrl + `/tracking/api/freight_orders/${this.id}/sign/${this.freightOrder.sign_file_id}`;

      this.$http({
        url: isSign ? signUrl : printUrl,
        method: 'GET',
        responseType: 'blob',
      })
        .then(response => {
          const href = window.URL.createObjectURL(new Blob([response.data]));
          const disposition = contentDisposition.parse(response.headers['content-disposition']);

          const link = document.createElement('a');
          link.href = href;
          link.setAttribute('download', disposition.parameters.filename);
          link.setAttribute('target', '_blank');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        });
    },
    downloadFileForStatus(statusId) {
      const printUrl = this.apiUrl + `/tracking/api/freight_orders/${this.id}/print_for_status/?status_id=${statusId}`;

      this.$http({
        url: printUrl,
        method: 'GET',
        responseType: 'blob',
      })
        .then(response => {
          const href = window.URL.createObjectURL(new Blob([response.data]));
          const disposition = contentDisposition.parse(response.headers['content-disposition']);

          const link = document.createElement('a');
          link.href = href;
          link.setAttribute('download', disposition.parameters.filename);
          link.setAttribute('target', '_blank');
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        });
    },

    updateConsigmentInfo(direction) {
      if (this.isFreightOrderUpdate || this.$route.query.copiedId) {
        return;
      }

      const resetConsigmentFields = type => {
        Array.from(Object.keys(this.freightOrder))
          .filter(key => key.includes(type))
          .forEach(it => {
            this.freightOrder[it] = '';
          });
      };

      if (direction === 'import') {
        this.freightOrder = {
          ...this.freightOrder,
          ...this.defaultImportInfo,
        };
        resetConsigmentFields('consignee');
      }

      if (direction === 'export' || direction === 'perevalka') {
        this.freightOrder = {
          ...this.freightOrder,
          ...this.defaultExportInfo,
        };
        resetConsigmentFields('consignor');
      }
    },
    updateConsigmentInfoFromModal(consigmentInfo) {
      this.freightOrder = {
        ...this.freightOrder,
        ...consigmentInfo,
      };

      Array.from(Object.keys(this.freightOrderErrors))
        .forEach(key => {
          if (key.includes(this.consigmentModalMode)) {
            delete this.freightOrderErrors[key];
          }
        });
    },
    updateStationsInfoFromModal(stationInfo) {
      this.freightOrder[this.stationsInfoModalField] = stationInfo;
    },
    updateCompanyInfo({ itemData }) {
      this.freightOrder.freight_forwarding_contract = itemData.freight_forwarding_contract;
      this.freightOrder.freight_forwarding_contract_date = itemData.freight_forwarding_contract_date;
    },
    updateDocumentSigned({ value }) {
      this.documentSigned = value[0];
    },
    onAutocompleteFocusIn(e, field) {
      if (!this.freightOrder[field]) {
        e.component.open();
      }
    },
    validatePackageBtns() {
      if (!this.$refs.containerGrid) {
        return false;
      }
      const [data] = this.$refs.containerGrid.instance.getSelectedRowsData();
      this.packageBtnsValid = !!data.pkg_pid;
    },
    showAutocompleteModal(field) {
      this.autocompleteModalField = field;
      this.isAutocompleteModalShown = true;
    },
    updateTextarea(val) {
      this.freightOrder[this.autocompleteModalField] = val;
    },
    validateContainersAdding() {
      this.containersAddingAllowed = this.freightOrder.container_count > this.$refs.containerGrid.instance.totalCount();
      this.leftContainers = this.freightOrder.container_count - this.$refs.containerGrid.instance.totalCount();
    },
    onContainersCellPrepared(cell) {
      if (!cell.value) {
        return;
      }

      if (cell.rowType === 'data' && cell.column.dataField === 'container' && !validateContainerNumber(cell.value)) {
        cell.cellElement.classList.add('dx-validator', 'dx-datagrid-invalid');
      }
    },
    resetFreightOrderValidation(field) {
      this.$set(this.freightOrderErrors, field, null);
    },
    async toogleIsPaid() {
      if (this.freightOrder.is_paid) {
        this.freightOrder.paid_date = null;
      }
      await this.changeIsPaid();
    },
    async changeIsPaid() {
      this.loading = true;
      try {
        await this.$http.patch(`/tracking/api/freight_orders/${this.freightOrder.id}/set_is_paid/`, {
          is_paid: !this.freightOrder.is_paid,
          paid_date: this.freightOrder.paid_date,
        });
        await this.getFreightOrder();
      } finally {
        this.loading = false;
      }
    },
    customizeTransportText(cellInfo) {
      if (!cellInfo.value) {
        return '';
      }
      return cellInfo.value.map(it => this.serviceTransportTypes.find(({ id }) => it === id).description).join(', ');
    },
    customizeShipmentText(cellInfo) {
      if (!cellInfo.value) {
        return '';
      }
      return cellInfo.value.map(it => this.serviceShipmentTypes.find(({ id }) => it === id).description).join(', ');
    },
    async getServiceShipmentTypes() {
      const { results } = await this.$http.get('service/api/shipment_kind/');
      this.serviceShipmentTypes = results;
    },
    async getServiceTransportTypes() {
      const { results } = await this.$http.get('service/api/transport_kind/');
      this.serviceTransportTypes = results;
    },
    async getServices() {
      const { results } = await this.$http.get(this.servicesUrl);
      this.services = results;

      this.freightOrder.ordered_services = this.freightOrder.ordered_services.filter(orderedServiceId => {
        return this.servicesSelected.find(service => service.id === orderedServiceId);
      });
    },
    async getSignedDocs() {
      const { results } = await this.$http.get(`/tracking/api/freight_orders/${this.freightOrder.id}/sign`);
      this.signedDocs = results;
    },
    async revisionSignedDocs() {
      const { results } = await this.$http.get(`/tracking/api/freight_orders/${this.freightOrder.id}/sign/?last=true`);
      this.revisionDocs = results;
    },
    removeFreightOrderedService({ key }) {
      this.freightOrder.ordered_services = this.freightOrder.ordered_services.filter((id) => id !== key.id);
    },
    addFreightOrderedServices(servicesSelected) {
      this.freightOrder.ordered_services = this.freightOrder.ordered_services.concat(servicesSelected);
    },
    async costDetailsInfo() {
      const { results } = await this.$http.get(`/tracking/api/freight_orders/${this.freightOrder.id}/cost-details/?ordering=id`);
      this.orderCostDetails = results;
    },
    async defaultCostDetailsInfo() {
      const { results } = await this.$http.get('/tracking/api/cost-details-defaults/');
      this.defaultCostDetails = results;
    },
    showStationsModal(field) {
      this.stationsInfoModalField = field;
      this.isStationsInfoModalShown = true;
    },

  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('refreshTable');
    next();
  },
};
</script>

<style lang="scss">
  .history-order-link,
  .history-signed-docs-link {
    font-size: 12px;
    font-weight: 600;
    color: #3C5AC3;
  }

  .cost__panel {
    background-color: #F4F8FA;
  }

  .main__form {
    flex: 4;
    margin-bottom: 90px;
  }

  .form__top-buttons {
    display: flex;
    align-items: center;
    margin-bottom: 40px;
  }

  .form__group {
    margin-bottom: 16px;
    padding: 25px;
    border: 2px solid #e0e6ee;
    border-radius: 8px;
    transition: width 0.5s;
  }

  .border-bottom {
    border-color: #e0e0e0;
  }

  .status-validation {
    margin-top: -25px;
    margin-bottom: 2rem;
  }

  .dx-dropdowneditor-icon.custom-dropdown {
    display: block;
    position: relative;
    width: 100%;
    height: 40px;
    border-top: 2px solid #e0e6ee;
    color: #3c5ac3;
    cursor: pointer;

    &:before {
      position: absolute;
      font-weight: bold;
    }

    &.active:before {
      content: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M16 13.9997L12 10.1426L8 13.9997' stroke='%233C5AC3' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    }
  }

  .dx-form-group-select a {
    width: 70px;
    height: 20px;
    font-size: 12px;
    text-align:center;
    color: #3C5AC3;
    &:hover,
    &:active {
      color: #2347C6;
      text-decoration: none;
    }
    &:focus {
      border: 2px solid #3C5AC3;
      border-radius: 4px;
    }
  }

  .signed-documents {
    .dx-row:not(.dx-header-row) td:nth-child(2) {
      color: #3C5AC3;
      text-decoration: underline;
    }
  }

  .dx-selection .dx-datagrid-invalid {
    background-color: #e91e6352 !important;
    color: #0e385e !important;
  }

  .dx-invalid-message {
    z-index: 1 !important;
  }

  .cost__is-paid.dx-state-disabled .dx-checkbox-text {
    color: #8E9EB6
  }

  .group__cost {
    display: flex;
    @media(max-width: 1930px) {
      flex-direction: row;
      column-gap: 2rem;
    }
    @media(max-width: 1400px) {
      flex-direction: row;
      column-gap: 1rem;
    }
    @media(max-width: 1250px) {
      flex-direction: row;
      column-gap: 0.5rem;
    }
    @media(max-width: 992px) {
      flex-direction: column;
      row-gap: 1rem;
    }
  }

  .cost__panel {
    border-radius: 8px;
    @media(max-width: 1930px) {
      padding: 2rem;
    }
    @media(max-width: 1400px) {
      padding: 2rem;
    }
    @media(max-width: 1250px) {
      padding: 1rem;
    }
    @media(max-width: 992px) {
      padding: 1rem;
    }
  }

  .cost__panel-header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    & .dx-button {
      background-color: transparent;
      box-shadow: none;
    }
    & .dx-button.dx-state-disabled {
      background-color: transparent;
    }
  }

  .cost__rate-list {
    font-size: 12px;
    padding-left: 1.6rem;
    list-style: none;
    & li {
      margin-bottom: 0.9rem;
      position: relative;
    }

    .rate-list__description {
      margin-bottom: 0.3rem;
    }
    .rate-list__body {
      margin-bottom: 1rem;
      font-weight: 400;
    }
    .rate-list__note {
      padding: 0.9rem;
      border-radius: 8px;
      background-color: #fff;
      font-size: 10px;
      font-weight: 400;
    }
  }

  .cost__included-list li::before {
    position: absolute;
    left: -30px;
    top: -3px;
    width: 24px;
    height: 24px;
    content: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M6 10.5L11 15.5L18.5 8' stroke='rgb(60,90,195)' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  }

  .cost__not-included-list li::before {
    position: absolute;
    left: -30px;
    top: -3px;
    width: 24px;
    height: 24px;
    content: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M18 6L6 18' stroke='rgb(232, 52, 52)' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3Cpath d='M6 6L18 18' stroke='rgb(232, 52, 52)' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E%0A");
  }

  .status__file-uploader {
    .dx-fileuploader-wrapper {
      min-height: 97px !important;
    }
    .dx-fileuploader-button {
      flex-direction: row !important;
      align-items: center;
    }

    .dx-fileuploader-input,
    .dx-fileuploader-input-label,
    .dx-fileuploader-input-container {
      padding: 0;
    }

    .dx-fileuploader-input,
    .dx-fileuploader-input-label {
      height: fit-content;
    }
  }

  .dx-datagrid .dx-datagrid-search-panel {
    margin-left: 0 ;
    width: 250px !important;
  }
</style>
